/**
 * My Portfolio Page (Leader Dashboard)
 *
 * 리더 포트폴리오 관리 페이지
 * - 포트폴리오 정보 및 통계
 * - 성과 차트 (ROI, Win Rate, Sharpe Ratio)
 * - 팔로워 수, 복사된 자금 총액
 * - 포트폴리오 설정 (공개/비공개, 수익 공유율)
 *
 * @see docs/COPY_TRADING_FRONTEND_PLAN.md
 */

import React, { useState, useEffect, useContext, useMemo } from 'react';
import styled from 'styled-components';
import { Button, Tag, Statistic, Progress, message, Modal, Input, InputNumber, Select } from 'antd';
import { useTranslation } from 'react-i18next';
import { ThemeContext } from '@/theme/ThemeProvider';
import { formatDecimal } from '@utils/formatDecimal';
import { getBigNumber } from '@utils/getBigNumber';

import { useMyPortfolio } from '../../hooks/copyTrading/useMyPortfolio';
import { updateMyPortfolio, createPortfolio } from '../../api/copyTrading';

import type { CopyPortfolio, CopyPortfolioStats } from '../../../types/copyTrading';

const { TextArea } = Input;

export function MyPortfolioPage() {
  const { t } = useTranslation();
  const { isDarkMode } = useContext(ThemeContext);

  // 포트폴리오 조회 (리더 탭 표시 조건)
  const { portfolio, hasPortfolio, isLoading: portfolioLoading, refetch } = useMyPortfolio();

  const [stats, setStats] = useState<CopyPortfolioStats | null>(null);
  const [summary, setSummary] = useState({
    totalFollowers: 0,
    totalCopiedAmount: 0,
    totalEarningsThisMonth: 0,
    totalEarningsAll: 0,
  });

  const [openEditModal, setOpenEditModal] = useState(false);
  const [openCreateModal, setOpenCreateModal] = useState(false);

  // 편집 폼 상태
  const [editForm, setEditForm] = useState({
    portfolioName: '',
    description: '',
    profitShareRate: 15,
    minCopyAmount: 100,
    maxFollowers: 500,
    status: 1 as 1 | 2 | 3,
  });

  // 생성 폼 상태
  const [createForm, setCreateForm] = useState({
    portfolioName: '',
    description: '',
    profitShareRate: 15,
    minCopyAmount: 100,
    maxFollowers: 500,
  });

  // ==========================================
  // Effects
  // ==========================================

  // portfolio가 변경될 때마다 summary 업데이트
  useEffect(() => {
    if (portfolio) {
      setSummary({
        totalFollowers: portfolio.currentFollowers || 0,
        totalCopiedAmount: 0, // MVP에서는 계산 안 함
        totalEarningsThisMonth: 0, // MVP에서는 0
        totalEarningsAll: 0, // MVP에서는 0
      });
    } else {
      setSummary({
        totalFollowers: 0,
        totalCopiedAmount: 0,
        totalEarningsThisMonth: 0,
        totalEarningsAll: 0,
      });
    }
  }, [portfolio]);

  // ==========================================
  // Handlers
  // ==========================================

  const handleOpenEditModal = () => {
    if (!portfolio) return;
    setEditForm({
      portfolioName: portfolio.portfolioName,
      description: portfolio.description || '',
      profitShareRate: portfolio.profitShareRate,
      minCopyAmount: portfolio.minCopyAmount,
      maxFollowers: portfolio.maxFollowers,
      status: portfolio.status,
    });
    setOpenEditModal(true);
  };

  const handleSaveEdit = async () => {
    if (!portfolio) return;

    try {
      const response = await updateMyPortfolio(editForm);

      if (response.success) {
        message.success(t('portfolioUpdated'));
        setOpenEditModal(false);
        await refetch();
      } else {
        message.error(t('updateFailed'));
      }
    } catch (error: any) {
      message.error(t('updateFailed'));
    }
  };

  const handleCreatePortfolio = async () => {
    try {
      const response = await createPortfolio(createForm);

      if (response.success) {
        message.success(t('portfolioCreated'));
        setOpenCreateModal(false);
        await refetch();
      } else {
        message.error(t('createFailed'));
      }
    } catch (error: any) {
      message.error(t('createFailed'));
    }
  };

  const handlePausePortfolio = async () => {
    if (!portfolio) return;

    Modal.confirm({
      title: t('pausePortfolioTitle'),
      content: t('pausePortfolioDesc'),
      okText: t('pause'),
      cancelText: t('cancel'),
      onOk: async () => {
        try {
          const response = await updateMyPortfolio({
            status: 2, // Paused
          } as any);

          if (response.success) {
            message.success(t('portfolioPaused'));
            await refetch();
          } else {
            message.error(t('pauseFailed'));
          }
        } catch (error: any) {
          message.error(t('pauseFailed'));
        }
      },
    });
  };

  const handleResumePortfolio = async () => {
    if (!portfolio) return;

    try {
      const response = await updateMyPortfolio({
        status: 1, // Active
      } as any);

      if (response.success) {
        message.success(t('portfolioResumed'));
        await refetch();
      } else {
        message.error(t('resumeFailed'));
      }
    } catch (error: any) {
      message.error(t('resumeFailed'));
    }
  };

  // ==========================================
  // Computed Values
  // ==========================================

  const statusInfo = useMemo(() => {
    if (!portfolio) return { text: 'N/A', color: 'default' };

    const statusMap = {
      1: { text: t('activeStatus'), color: 'green' },
      2: { text: t('pausedStatus'), color: 'orange' },
      3: { text: t('closedStatus'), color: 'red' },
    };

    return statusMap[portfolio.status as 1 | 2 | 3] || { text: 'Unknown', color: 'default' };
  }, [portfolio, t]);

  const followerCapacityPercent = useMemo(() => {
    if (!portfolio) return 0;
    return getBigNumber(portfolio.currentFollowers).dividedBy(portfolio.maxFollowers).multipliedBy(100).toNumber();
  }, [portfolio]);

  // ==========================================
  // Render: Empty State (포트폴리오 없음)
  // ==========================================

  if (!portfolioLoading && !portfolio) {
    return (
      <PageWrapper>
        <EmptyState isDark={isDarkMode}>
          <EmptyIcon>📊</EmptyIcon>
          <h2>{t('noLeaderPortfolio')}</h2>
          <p style={{ fontSize: 14, opacity: 0.7, marginBottom: 24 }}>
            {t('noLeaderPortfolioDesc')}
          </p>
          <Button type="primary" size="large" onClick={() => setOpenCreateModal(true)}>
            {t('createPortfolio')}
          </Button>
        </EmptyState>

        {/* Create Portfolio Modal */}
        <Modal
          title={t('createPortfolio')}
          open={openCreateModal}
          onOk={handleCreatePortfolio}
          onCancel={() => setOpenCreateModal(false)}
          okText={t('createPortfolio')}
          cancelText={t('cancel')}
          width={600}
        >
          <ModalContent>
            <FormGroup>
              <label>{t('portfolioName')} *</label>
              <Input
                placeholder={t('portfolioNamePlaceholder')}
                value={createForm.portfolioName}
                onChange={(e) => setCreateForm({ ...createForm, portfolioName: e.target.value })}
              />
            </FormGroup>

            <FormGroup>
              <label>{t('description')}</label>
              <TextArea
                rows={3}
                placeholder={t('descriptionPlaceholder')}
                value={createForm.description}
                onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
              />
            </FormGroup>

            <FormRow>
              <FormGroup>
                <label>{t('profitShareRateLabel')}</label>
                <InputNumber
                  min={5}
                  max={30}
                  value={createForm.profitShareRate}
                  onChange={(value) => setCreateForm({ ...createForm, profitShareRate: value || 15 })}
                  style={{ width: '100%' }}
                  addonAfter="%"
                />
                <FormHint>{t('between5and30')}</FormHint>
              </FormGroup>

              <FormGroup>
                <label>{t('minCopyAmount')} *</label>
                <InputNumber
                  min={10}
                  value={createForm.minCopyAmount}
                  onChange={(value) => setCreateForm({ ...createForm, minCopyAmount: value || 100 })}
                  style={{ width: '100%' }}
                  addonAfter="USDT"
                />
              </FormGroup>
            </FormRow>

            <FormGroup>
              <label>{t('maxFollowers')} *</label>
              <InputNumber
                min={1}
                max={5000}
                value={createForm.maxFollowers}
                onChange={(value) => setCreateForm({ ...createForm, maxFollowers: value || 500 })}
                style={{ width: '100%' }}
              />
            </FormGroup>
          </ModalContent>
        </Modal>
      </PageWrapper>
    );
  }

  // ==========================================
  // Render: Portfolio Dashboard
  // ==========================================

  return (
    <PageWrapper>
      {/* Page Header */}
      <CompactHeader isDark={isDarkMode}>
        <HeaderLeft>
          <HeaderTitleRow>
            <HeaderTitle>{portfolio?.portfolioName || 'My Portfolio'}</HeaderTitle>
            <Tag color={statusInfo.color}>{statusInfo.text}</Tag>
          </HeaderTitleRow>
          <HeaderDescription>{portfolio?.description || 'No description'}</HeaderDescription>
        </HeaderLeft>

        <StatsRow>
          <StatItem>
            <Button onClick={handleOpenEditModal}>{t('settings')}</Button>
          </StatItem>
          <StatDivider />
          {portfolio?.status === 1 && (
            <StatItem>
              <Button onClick={handlePausePortfolio}>{t('pause')}</Button>
            </StatItem>
          )}
          {portfolio?.status === 2 && (
            <StatItem>
              <Button type="primary" onClick={handleResumePortfolio}>{t('resume')}</Button>
            </StatItem>
          )}
        </StatsRow>
      </CompactHeader>

      {/* Summary Cards */}
      <SummaryGrid>
        <SummaryCard isDark={isDarkMode}>
          <Statistic
            title={t('totalFollowers')}
            value={summary.totalFollowers}
            suffix={`/ ${portfolio?.maxFollowers || 0}`}
          />
          <Progress
            percent={followerCapacityPercent}
            showInfo={false}
            strokeColor="#ff9131"
            style={{ marginTop: 12 }}
          />
        </SummaryCard>

        <SummaryCard isDark={isDarkMode}>
          <Statistic
            title={t('totalCopiedAmount')}
            value={formatDecimal(summary.totalCopiedAmount, 2)}
            suffix="USDT"
          />
        </SummaryCard>

        <SummaryCard isDark={isDarkMode}>
          <Statistic
            title={t('thisMonthEarnings')}
            value={formatDecimal(summary.totalEarningsThisMonth, 2)}
            suffix="USDT"
            valueStyle={{ color: '#22c55e' }}
          />
        </SummaryCard>

        <SummaryCard isDark={isDarkMode}>
          <Statistic
            title={t('totalEarningsAllTime')}
            value={formatDecimal(summary.totalEarningsAll, 2)}
            suffix="USDT"
            valueStyle={{ color: '#22c55e' }}
          />
        </SummaryCard>
      </SummaryGrid>

      {/* Stats Section */}
      <Section isDark={isDarkMode}>
        <SectionTitle>{t('performanceMetrics')}</SectionTitle>
        <StatsGrid>
          <StatCard isDark={isDarkMode}>
            <StatLabel>{t('totalTrades')}</StatLabel>
            <StatValue>{stats?.totalTrades || 0}</StatValue>
          </StatCard>

          <StatCard isDark={isDarkMode}>
            <StatLabel>{t('winRate')}</StatLabel>
            <StatValue style={{ color: '#22c55e' }}>{formatDecimal(stats?.winRate || 0, 1)}%</StatValue>
          </StatCard>

          <StatCard isDark={isDarkMode}>
            <StatLabel>{t('averageProfit')}</StatLabel>
            <StatValue>{formatDecimal(stats?.averageProfit || 0, 2)} USDT</StatValue>
          </StatCard>

          <StatCard isDark={isDarkMode}>
            <StatLabel>{t('maxDrawdown')}</StatLabel>
            <StatValue style={{ color: '#ef4444' }}>{formatDecimal(stats?.maxDrawdown || 0, 1)}%</StatValue>
          </StatCard>

          <StatCard isDark={isDarkMode}>
            <StatLabel>{t('sharpeRatio')}</StatLabel>
            <StatValue>{formatDecimal(stats?.sharpeRatio || 0, 2)}</StatValue>
          </StatCard>

          <StatCard isDark={isDarkMode}>
            <StatLabel>{t('roi30d')}</StatLabel>
            <StatValue style={{ color: '#22c55e' }}>
              {formatDecimal(stats?.roi30d || 0, 1)}%
            </StatValue>
          </StatCard>

          <StatCard isDark={isDarkMode}>
            <StatLabel>{t('roi90d')}</StatLabel>
            <StatValue style={{ color: '#22c55e' }}>
              {formatDecimal(stats?.roi90d || 0, 1)}%
            </StatValue>
          </StatCard>

          <StatCard isDark={isDarkMode}>
            <StatLabel>{t('roiAll')}</StatLabel>
            <StatValue style={{ color: '#22c55e' }}>
              {formatDecimal(stats?.roiAll || 0, 1)}%
            </StatValue>
          </StatCard>
        </StatsGrid>
      </Section>

      {/* Portfolio Settings Section */}
      <Section isDark={isDarkMode}>
        <SectionTitle>{t('portfolioSettings')}</SectionTitle>
        <SettingsGrid>
          <SettingItem>
            <SettingLabel>{t('profitShareRate')}</SettingLabel>
            <SettingValue>{portfolio?.profitShareRate || 0}%</SettingValue>
          </SettingItem>

          <SettingItem>
            <SettingLabel>{t('minCopyAmount')}</SettingLabel>
            <SettingValue>{formatDecimal(portfolio?.minCopyAmount || 0, 0)} USDT</SettingValue>
          </SettingItem>

          <SettingItem>
            <SettingLabel>{t('maxFollowers')}</SettingLabel>
            <SettingValue>{portfolio?.maxFollowers || 0}</SettingValue>
          </SettingItem>

          <SettingItem>
            <SettingLabel>{t('createdDate')}</SettingLabel>
            <SettingValue>
              {portfolio?.createdAt ? new Date(portfolio.createdAt).toLocaleDateString() : 'N/A'}
            </SettingValue>
          </SettingItem>
        </SettingsGrid>
      </Section>

      {/* Edit Modal */}
      <Modal
        title={t('editPortfolio')}
        open={openEditModal}
        onOk={handleSaveEdit}
        onCancel={() => setOpenEditModal(false)}
        okText={t('save')}
        cancelText={t('cancel')}
        width={600}
      >
        <ModalContent>
          <FormGroup>
            <label>{t('portfolioName')}</label>
            <Input
              value={editForm.portfolioName}
              onChange={(e) => setEditForm({ ...editForm, portfolioName: e.target.value })}
            />
          </FormGroup>

          <FormGroup>
            <label>{t('description')}</label>
            <TextArea
              rows={3}
              value={editForm.description}
              onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
            />
          </FormGroup>

          <FormRow>
            <FormGroup>
              <label>{t('profitShareRateLabel')}</label>
              <InputNumber
                min={5}
                max={30}
                value={editForm.profitShareRate}
                onChange={(value) => setEditForm({ ...editForm, profitShareRate: value || 15 })}
                style={{ width: '100%' }}
                addonAfter="%"
              />
            </FormGroup>

            <FormGroup>
              <label>{t('minCopyAmount')}</label>
              <InputNumber
                min={10}
                value={editForm.minCopyAmount}
                onChange={(value) => setEditForm({ ...editForm, minCopyAmount: value || 100 })}
                style={{ width: '100%' }}
                addonAfter="USDT"
              />
            </FormGroup>
          </FormRow>

          <FormGroup>
            <label>{t('maxFollowers')}</label>
            <InputNumber
              min={1}
              max={5000}
              value={editForm.maxFollowers}
              onChange={(value) => setEditForm({ ...editForm, maxFollowers: value || 500 })}
              style={{ width: '100%' }}
            />
          </FormGroup>

          <FormGroup>
            <label>{t('status')}</label>
            <Select
              value={editForm.status}
              onChange={(value) => setEditForm({ ...editForm, status: value })}
              style={{ width: '100%' }}
            >
              <Select.Option value={1}>{t('activeStatus')}</Select.Option>
              <Select.Option value={2}>{t('pausedStatus')}</Select.Option>
              <Select.Option value={3}>{t('closedStatus')}</Select.Option>
            </Select>
          </FormGroup>
        </ModalContent>
      </Modal>
    </PageWrapper>
  );
}

// ==========================================
// Styled Components
// ==========================================

const PageWrapper = styled.div`
  display: flex;
  max-width: 1200px;
  width: 100%;
  flex-direction: column;
  align-items: flex-start;
  gap: 0;

  @media (max-width: 1240px) {
    padding: 20px;
  }
`;

const CompactHeader = styled.div<{ isDark: boolean }>`
  width: 100%;
  display: grid;
  grid-template-columns: minmax(200px, 1fr) auto;
  align-items: center;
  gap: 24px;
  margin-bottom: 20px;
  padding: 20px 24px;
  box-sizing: border-box;
  background: ${({ isDark }) =>
    isDark
      ? 'linear-gradient(135deg, rgba(255, 145, 49, 0.05) 0%, rgba(20, 17, 34, 0.05) 100%), #1a1a1a'
      : 'linear-gradient(135deg, rgba(255, 145, 49, 0.03) 0%, rgba(255, 255, 255, 1) 100%)'
  };
  border-radius: 12px;
  border: ${({ isDark }) => (isDark ? '1px solid #333' : '1px solid #e0e0e0')};

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 12px;
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const HeaderTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const HeaderTitle = styled.h1`
  font-size: 20px;
  font-weight: 700;
  margin: 0;
  color: ${({ theme }) => theme.text};
`;

const HeaderDescription = styled.p`
  margin: 0;
  font-size: 13px;
  color: ${({ theme }) => theme.text};
  opacity: 0.6;
  max-width: 600px;
`;

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: auto auto auto;
  align-items: center;
  gap: 12px;
  justify-self: end;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 8px;
    width: 100%;
    justify-self: stretch;
  }
`;

const StatItem = styled.div`
  display: flex;
  align-items: center;
`;

const StatDivider = styled.div`
  width: 1px;
  height: 32px;
  background-color: ${({ theme }) => theme.text};
  opacity: 0.1;

  @media (max-width: 768px) {
    display: none;
  }
`;

const SummaryGrid = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
`;

const SummaryCard = styled.div<{ isDark: boolean }>`
  width: 100%;
  padding: 24px;
  box-sizing: border-box;
  border-radius: 12px;
  background: ${({ isDark }) =>
    isDark
      ? 'linear-gradient(135deg, rgba(255, 145, 49, 0.03) 0%, rgba(20, 17, 34, 0.03) 100%), #1a1a1a'
      : 'linear-gradient(135deg, rgba(255, 145, 49, 0.02) 0%, rgba(255, 255, 255, 1) 100%)'
  };
  border: ${({ isDark }) => (isDark ? '1px solid #333' : '1px solid #f0f0f0')};
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${({ isDark }) =>
      isDark ? '0 4px 12px rgba(0, 0, 0, 0.3)' : '0 4px 12px rgba(0, 0, 0, 0.08)'};
  }

  .ant-statistic-title {
    color: ${({ theme }) => theme.text};
    opacity: 0.7;
  }

  .ant-statistic-content {
    color: ${({ theme }) => theme.text};
  }
`;

const Section = styled.div<{ isDark: boolean }>`
  width: 100%;
  margin-bottom: 32px;
  padding: 24px;
  box-sizing: border-box;
  border-radius: 12px;
  background-color: ${({ isDark }) => (isDark ? '#1a1a1a' : '#fff')};
  border: ${({ isDark }) => (isDark ? '1px solid #333' : '1px solid #e0e0e0')};
`;

const SectionTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 20px;
  color: ${({ theme }) => theme.text};
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 16px;
`;

const StatCard = styled.div<{ isDark: boolean }>`
  padding: 16px;
  border-radius: 8px;
  background: ${({ isDark }) => (isDark ? '#242424' : '#f9f9f9')};
  border: ${({ isDark }) => (isDark ? '1px solid #333' : '1px solid #e0e0e0')};
`;

const StatLabel = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.text};
  opacity: 0.7;
  margin-bottom: 8px;
`;

const StatValue = styled.div`
  font-size: 20px;
  font-weight: 600;
  color: ${({ theme }) => theme.text};
`;

const SettingsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
`;

const SettingItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SettingLabel = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.text};
  opacity: 0.7;
`;

const SettingValue = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.text};
`;

const EmptyState = styled.div<{ isDark: boolean }>`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  box-sizing: border-box;
  background: ${({ isDark }) => (isDark ? '#1a1a1a' : '#fff')};
  border-radius: 12px;
  border: ${({ isDark }) => (isDark ? '1px solid #333' : '1px solid #e0e0e0')};

  h2 {
    font-size: 24px;
    font-weight: 600;
    margin: 16px 0 8px 0;
    color: ${({ theme }) => theme.text};
  }

  p {
    color: ${({ theme }) => theme.text};
  }
`;

const EmptyIcon = styled.div`
  font-size: 64px;
`;

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px 0;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  label {
    font-size: 14px;
    font-weight: 500;
    color: ${({ theme }) => theme.text};
  }

  textarea {
    &::-webkit-resizer {
        background-color: #f5f5f5;
        border-radius: 4px;
      }
    }
  }
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
`;

const FormHint = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.text};
  opacity: 0.6;
`;
